import React, { useState } from "react";

import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Box,
  Paper,
  Divider,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDeepCompareEffect } from "@fuse/hooks";
import {
  getClientResponses,
  getReportById,
  selectReportById,
} from "../../reports/store/reportSlice";
import FuseLoading from "@fuse/core/FuseLoading";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { addClientResponse } from "../../reports/store/reportSlice";
import { ApiUserAdmin, endpoints_auditAdmin } from "app/api/userAdminApi";

const categorys = [
  { nombre: "INSTALACIONES", rango: [1, 16] },
  { nombre: "LIMPIEZA Y SANITIZACIÓN", rango: [17, 20] },
  { nombre: "CONTROL DE PLAGAS", rango: [21, 23] },
  { nombre: "HIGIENE DEL PERSONAL", rango: [24, 26] },
  { nombre: "CAPACITACIÓN", rango: [27, 28] },
  { nombre: "MATERIAS PRIMAS", rango: [29, 33] },
  { nombre: "PROCESOS Y PRODUCTO TERMINADO", rango: [34, 39] },
];

const isPremium = true;

const api = new ApiUserAdmin();

const ViewReportImages = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const [answersByCategory, setAnswersByCategory] = React.useState([]);
  const [page, setPage] = useState(0);
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
    }
  };
  const handleSubmitResponse = async (subformIndex, auditId) => {
    try {
      setIsSubmitting(true); // 🚀 Empieza el loader
      await dispatch(
        addClientResponse({ auditId, subformIndex, comment, photo })
      );

      // 🔄 Recargar datos del reporte y respuestas del cliente
      await Promise.all([
        dispatch(getReportById(auditId)),
        dispatch(getClientResponses(auditId)),
      ]);

      setComment("");
      setPhoto(null);
    } catch (error) {
      console.error("Error al enviar respuesta:", error);
    } finally {
      setIsSubmitting(false); // 🟢 Detiene el loader
    }
  };
  const navigate = useNavigate();

  const { reportId } = params;

  const report = useSelector((state) => selectReportById(state, reportId));

  const clientResponses = report?.clientResponses || [];

  React.useEffect(() => {
    if (report) {
      const finders = report.answers.filter((a) =>
        a.question_name.toUpperCase().includes("HALLAZGOS")
      );

      const grouped = finders.reduce((acc, item) => {
        const category = item.question_name
          .replace(/^\d+\.\s*/, "")
          .replace(/\s*HALLAZGOS/i, "")
          .trim();

        if (!acc[category]) {
          acc[category] = {};
        }

        const subIndex = item.subform_index ?? 0;
        if (!acc[category][subIndex]) {
          acc[category][subIndex] = [];
        }

        const response = report.clientResponses?.find(
          (r) => r.subform_index == subIndex && r.audit_id == reportId
        );

        acc[category][subIndex].push({ item: item, client_response: response });

        return acc;
      }, {});

      const groupedArray = Object.entries(grouped).map(
        ([category, subforms]) => ({
          category,
          groups: Object.entries(subforms).map(([subIndex, items]) => ({
            subform_index: parseInt(subIndex, 10),
            items,
          })),
        })
      );

      setAnswersByCategory(groupedArray);
      setPage(0); // 🚀 Reiniciar siempre a la primera categoría
    }
  }, [report]);

  useDeepCompareEffect(() => {
    dispatch(getReportById(reportId));
    dispatch(getClientResponses(reportId));
  }, [dispatch]);

  if (!report) {
    return <FuseLoading />;
  }

  const exportImagesReport = async (report, answersByCategory) => {
    const body = {
      audit: report,
      categories: answersByCategory,
    };
    const pdf = await api.postBlob(
      endpoints_auditAdmin.EXPORT_IMAGES_REPORT,
      body
    );
    const url = window.URL.createObjectURL(pdf);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `reporte_${report.id || "auditoria"}.pdf`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const currentCategory = answersByCategory[page] || null;

  const handleBack = () => history.back();

  return (
    <Box p={3}>
      <Button
        style={{ marginBottom: "10px" }}
        variant="contained"
        color="info"
        onClick={handleBack}
        size="small"
        startIcon={
          <FuseSvgIcon size={20}>material-solid:arrow_back</FuseSvgIcon>
        }
      >
        Volver
      </Button>

      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={() => exportImagesReport(report, answersByCategory)}
      >
        Exportar PDF
      </Button>

      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{ fontWeight: "bold", color: "primary.main" }}
      >
        {report.name}: {report.code}
      </Typography>
      <Typography align="center" variant="subtitle1" gutterBottom>
        Realizada por <strong>{report.user_name}</strong>
      </Typography>

      {currentCategory && (
        <Paper elevation={3} sx={{ p: 4, mt: 3, borderRadius: 3 }}>
          {/* Encabezado de categoría */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
          >
            Informe: {currentCategory.category}
          </Typography>

          {currentCategory.groups.map((group, gi) => {
            // buscar una respuesta del cliente del grupo (cualquiera que exista)
            const clientResponse = group.items.find(
              (i) => i.client_response
            )?.client_response;

            return (
              <Box
                key={group.subform_index}
                sx={{
                  mb: 5,
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: gi % 2 === 0 ? "grey.50" : "white",
                  borderTop: "4px solid",
                  borderColor: "primary.light",
                }}
              >
                {/* Título del grupo */}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    color: "secondary.main",
                    mb: 2,
                  }}
                >
                  Grupo {gi + 1}
                </Typography>

                {/* Items del grupo */}
                {group.items.map((item, i) => {
                  console.log("🚀 ~ ViewReportImages ~ item:", item)
                  return <Box key={item.item.id} sx={{ mb: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Punto {i + 1}: {item.item.name}
                    </Typography>

                    {item.item.question_type === "photo" ? (
                      <Box sx={{ textAlign: "center", mb: 1 }}>
                        <img
                          src={item.item.question_value}
                          alt={item.item.name || "Foto"}
                          style={{
                            maxWidth: "100%",
                            borderRadius: "10px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ ml: 1 }}>
                        {item.item.question_value}
                      </Typography>
                    )}
                    <Divider sx={{ mt: 3 }} />
                  </Box>;
                })}

                {clientResponse ? (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderLeft: "4px solid",
                      borderColor: "success.main",
                      backgroundColor: "success.light",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "success.dark" }}
                    >
                      Respuesta del cliente:
                    </Typography>

                    {clientResponse.image_url && (
                      <Box sx={{ textAlign: "center", mt: 1 }}>
                        <img
                          src={clientResponse.image_url}
                          alt="Respuesta cliente"
                          style={{
                            maxWidth: "100%",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                          }}
                        />
                      </Box>
                    )}

                    {clientResponse.comment && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {clientResponse.comment}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      border: "2px dashed",
                      borderColor: "info.main",
                      borderRadius: 2,
                      backgroundColor: "info.lighter",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "info.dark", mb: 2 }}
                    >
                      Tu respuesta
                    </Typography>

                    <TextField
                      label="Comentario"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      sx={{ mb: 2 }}
                    />

                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ mb: 2 }}
                      color="info"
                    >
                      Subir foto de evidencia
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                      />
                    </Button>

                    <Box>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={() =>
                          handleSubmitResponse(group.subform_index, report.id)
                        }
                        disabled={isSubmitting} // 🟠 Desactivado mientras se envía
                        startIcon={
                          isSubmitting ? (
                            <FuseSvgIcon size={20}>
                              material-outline:hourglass_empty
                            </FuseSvgIcon>
                          ) : null
                        }
                      >
                        {isSubmitting ? "Enviando..." : "Enviar respuesta"}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
          {/* Navegación */}
          <Box mt={4} display="flex" justifyContent="center" gap={2}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={page === answersByCategory.length - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ViewReportImages;
